/**
 * Formula Parser — Recursive Descent Parser (Frontend)
 * 
 * Replaces all regex-based tokenization with a deterministic, character-by-character
 * lexer and a recursive descent parser that produces an AST.
 * 
 * Grammar:
 *   Expression     → Term (('+' | '-') Term)*
 *   Term           → UnaryExpr (('*' | '/') UnaryExpr)*
 *   UnaryExpr      → ('-' UnaryExpr) | Atom
 *   Atom           → NUMBER | VARIABLE | '(' Expression ')'
 *   VARIABLE       → Identifier ('.' Identifier)*
 *   Identifier     → [a-zA-Z_][a-zA-Z0-9_]*
 *   NUMBER         → [0-9]+ ('.' [0-9]+)? ([eE][+-]?[0-9]+)?
 * 
 * Token types: NUMBER, VARIABLE, OPERATOR, LPAREN, RPAREN, EOF
 * 
 * Mirror of: backend/src/lib/formulaParser.js
 */

// ─── Token Types ───────────────────────────────────────────────────────────────

export const TokenType = {
    NUMBER: 'NUMBER',
    VARIABLE: 'VARIABLE',
    OPERATOR: 'OPERATOR',
    LPAREN: 'LPAREN',
    RPAREN: 'RPAREN',
    EOF: 'EOF',
} as const;

export type TokenTypeValue = typeof TokenType[keyof typeof TokenType];

export interface Token {
    type: TokenTypeValue;
    value: string;
    start: number;
    end: number;
}

// ─── AST Node Types ────────────────────────────────────────────────────────────

export const NodeType = {
    NUMBER: 'NumberLiteral',
    VARIABLE: 'VariableRef',
    BINARY: 'BinaryExpr',
    UNARY: 'UnaryExpr',
} as const;

export interface NumberNode {
    type: typeof NodeType.NUMBER;
    value: number;
}

export interface VariableNode {
    type: typeof NodeType.VARIABLE;
    path: string;
}

export interface BinaryNode {
    type: typeof NodeType.BINARY;
    operator: string;
    left: ASTNode;
    right: ASTNode;
}

export interface UnaryNode {
    type: typeof NodeType.UNARY;
    operator: string;
    operand: ASTNode;
}

export type ASTNode = NumberNode | VariableNode | BinaryNode | UnaryNode;

// ─── Character Helpers ─────────────────────────────────────────────────────────

function isDigit(ch: string): boolean {
    return ch >= '0' && ch <= '9';
}

function isIdentStart(ch: string): boolean {
    return (ch >= 'a' && ch <= 'z') || (ch >= 'A' && ch <= 'Z') || ch === '_';
}

function isIdentChar(ch: string): boolean {
    return isIdentStart(ch) || isDigit(ch);
}

// ─── Lexer ─────────────────────────────────────────────────────────────────────

/**
 * Scans a formula string character-by-character and produces an array of tokens.
 * No regex is used — this is fully deterministic.
 */
export function lex(input: string): { tokens: Token[]; errors: string[] } {
    const tokens: Token[] = [];
    const errors: string[] = [];
    let pos = 0;

    while (pos < input.length) {
        const ch = input[pos];

        // Skip whitespace
        if (ch === ' ' || ch === '\t' || ch === '\n' || ch === '\r') {
            pos++;
            continue;
        }

        // Numbers: [0-9]+ ('.' [0-9]+)? ([eE][+-]?[0-9]+)?
        if (isDigit(ch)) {
            const start = pos;
            while (pos < input.length && isDigit(input[pos])) pos++;

            if (pos < input.length && input[pos] === '.') {
                if (pos + 1 < input.length && isDigit(input[pos + 1])) {
                    pos++; // consume '.'
                    while (pos < input.length && isDigit(input[pos])) pos++;
                }
            }

            // Scientific notation
            if (pos < input.length && (input[pos] === 'e' || input[pos] === 'E')) {
                pos++;
                if (pos < input.length && (input[pos] === '+' || input[pos] === '-')) pos++;
                while (pos < input.length && isDigit(input[pos])) pos++;
            }

            tokens.push({
                type: TokenType.NUMBER,
                value: input.substring(start, pos),
                start,
                end: pos,
            });
            continue;
        }

        // Identifiers / Variables
        if (isIdentStart(ch)) {
            const start = pos;
            while (pos < input.length && isIdentChar(input[pos])) pos++;

            // Consume dot-separated segments
            while (pos < input.length && input[pos] === '.') {
                const dotPos = pos;
                pos++;

                if (pos < input.length && isIdentChar(input[pos])) {
                    while (pos < input.length && isIdentChar(input[pos])) pos++;
                } else {
                    pos = dotPos;
                    break;
                }
            }

            tokens.push({
                type: TokenType.VARIABLE,
                value: input.substring(start, pos),
                start,
                end: pos,
            });
            continue;
        }

        // Operators
        if (ch === '+' || ch === '-' || ch === '*' || ch === '/') {
            tokens.push({ type: TokenType.OPERATOR, value: ch, start: pos, end: pos + 1 });
            pos++;
            continue;
        }

        // Parentheses
        if (ch === '(') {
            tokens.push({ type: TokenType.LPAREN, value: '(', start: pos, end: pos + 1 });
            pos++;
            continue;
        }
        if (ch === ')') {
            tokens.push({ type: TokenType.RPAREN, value: ')', start: pos, end: pos + 1 });
            pos++;
            continue;
        }

        // Standalone dot
        if (ch === '.') {
            errors.push(`Unexpected '.' at position ${pos}`);
            pos++;
            continue;
        }

        errors.push(`Unexpected character '${ch}' at position ${pos}`);
        pos++;
    }

    tokens.push({ type: TokenType.EOF, value: '', start: pos, end: pos });
    return { tokens, errors };
}

// ─── Parser ────────────────────────────────────────────────────────────────────

/**
 * Parses tokens into an AST using recursive descent.
 */
export function parse(tokens: Token[]): { ast: ASTNode | null; errors: string[] } {
    let pos = 0;
    const errors: string[] = [];

    function peek(): Token {
        return tokens[pos] || { type: TokenType.EOF, value: '', start: 0, end: 0 };
    }

    function consume(): Token {
        return tokens[pos++];
    }

    function expect(type: TokenTypeValue, value?: string): Token | null {
        const tok = peek();
        if (tok.type !== type || (value !== undefined && tok.value !== value)) {
            errors.push(`Expected ${value || type} at position ${tok.start || pos}, got '${tok.value}'`);
            return null;
        }
        return consume();
    }

    // Expression → Term (('+' | '-') Term)*
    function parseExpression(): ASTNode | null {
        let left = parseTerm();
        if (!left) return null;

        while (peek().type === TokenType.OPERATOR && (peek().value === '+' || peek().value === '-')) {
            const op = consume();
            const right = parseTerm();
            if (!right) {
                errors.push(`Expected expression after '${op.value}' at position ${op.start}`);
                return left;
            }
            left = { type: NodeType.BINARY, operator: op.value, left, right };
        }

        return left;
    }

    // Term → UnaryExpr (('*' | '/') UnaryExpr)*
    function parseTerm(): ASTNode | null {
        let left = parseUnary();
        if (!left) return null;

        while (peek().type === TokenType.OPERATOR && (peek().value === '*' || peek().value === '/')) {
            const op = consume();
            const right = parseUnary();
            if (!right) {
                errors.push(`Expected expression after '${op.value}' at position ${op.start}`);
                return left;
            }
            left = { type: NodeType.BINARY, operator: op.value, left, right };
        }

        return left;
    }

    // UnaryExpr → ('-' UnaryExpr) | Atom
    function parseUnary(): ASTNode | null {
        if (peek().type === TokenType.OPERATOR && peek().value === '-') {
            const op = consume();
            const operand = parseUnary();
            if (!operand) {
                errors.push(`Expected expression after unary '-' at position ${op.start}`);
                return null;
            }
            return { type: NodeType.UNARY, operator: '-', operand };
        }
        return parseAtom();
    }

    // Atom → NUMBER | VARIABLE | '(' Expression ')'
    function parseAtom(): ASTNode | null {
        const tok = peek();

        if (tok.type === TokenType.NUMBER) {
            consume();
            return { type: NodeType.NUMBER, value: parseFloat(tok.value) };
        }

        if (tok.type === TokenType.VARIABLE) {
            consume();
            return { type: NodeType.VARIABLE, path: tok.value };
        }

        if (tok.type === TokenType.LPAREN) {
            consume();
            const expr = parseExpression();
            if (!expr) {
                errors.push(`Expected expression inside parentheses at position ${tok.start}`);
                return null;
            }
            const closing = expect(TokenType.RPAREN);
            if (!closing) {
                errors.push(`Missing closing ')' for '(' at position ${tok.start}`);
            }
            return expr;
        }

        if (tok.type === TokenType.EOF) {
            return null;
        }

        errors.push(`Unexpected token '${tok.value}' at position ${tok.start}`);
        consume();
        return null;
    }

    const ast = parseExpression();

    if (peek().type !== TokenType.EOF) {
        errors.push(`Unexpected token '${peek().value}' at position ${peek().start}`);
    }

    return { ast, errors };
}

// ─── AST Evaluation ────────────────────────────────────────────────────────────

/**
 * Evaluates an AST node given a context map.
 * No `new Function()` — pure, safe tree-walk evaluation.
 */
export function evaluateAST(node: ASTNode | null, context: Record<string, number>): number {
    if (!node) return 0;

    switch (node.type) {
        case NodeType.NUMBER:
            return node.value;

        case NodeType.VARIABLE: {
            const key = node.path.toLowerCase();
            const val = context[key];
            return val !== undefined ? val : 0;
        }

        case NodeType.BINARY: {
            const left = evaluateAST(node.left, context);
            const right = evaluateAST(node.right, context);
            switch (node.operator) {
                case '+': return left + right;
                case '-': return left - right;
                case '*': return left * right;
                case '/': return right === 0 ? 0 : left / right;
                default: return 0;
            }
        }

        case NodeType.UNARY: {
            const operand = evaluateAST(node.operand, context);
            if (node.operator === '-') return -operand;
            return operand;
        }

        default:
            return 0;
    }
}

// ─── Variable Extraction ───────────────────────────────────────────────────────

/**
 * Walks the AST and collects all variable reference paths.
 */
export function extractVariables(node: ASTNode | null): string[] {
    if (!node) return [];

    switch (node.type) {
        case NodeType.VARIABLE:
            return [node.path];
        case NodeType.BINARY:
            return [...extractVariables(node.left), ...extractVariables(node.right)];
        case NodeType.UNARY:
            return extractVariables(node.operand);
        case NodeType.NUMBER:
        default:
            return [];
    }
}

// ─── Lex Tokens (for Validator/Editor) ─────────────────────────────────────────

/**
 * Returns just the tokens array from the lexer.
 */
export function lexFormula(formula: string): Token[] {
    const { tokens } = lex(formula);
    return tokens;
}

// ─── High-Level API ────────────────────────────────────────────────────────────

/**
 * Full parse: lex + parse + return AST and any errors.
 */
export function parseFormula(formula: string): { ast: ASTNode | null; tokens: Token[]; errors: string[] } {
    const { tokens, errors: lexErrors } = lex(formula);
    const { ast, errors: parseErrors } = parse(tokens);
    return { ast, tokens, errors: [...lexErrors, ...parseErrors] };
}

/**
 * Extract all variable paths from a formula string.
 * Drop-in replacement for the old regex-based tokenize().
 */
export function tokenize(formula: string): string[] {
    if (!formula) return [];
    const { ast } = parseFormula(formula);
    return extractVariables(ast);
}

/**
 * Evaluate a formula with a given context.
 * Drop-in replacement for the old regex + new Function() evaluate().
 */
export function evaluate(formula: string, context: Record<string, number>): number {
    if (!formula) return 0;
    try {
        const { ast, errors } = parseFormula(formula);
        if (errors.length > 0 || !ast) {
            return 0;
        }
        const result = evaluateAST(ast, context);
        if (typeof result !== 'number' || !isFinite(result)) {
            return 0;
        }
        return result;
    } catch (e) {
        console.error('Formula evaluation error:', e, { formula });
        return 0;
    }
}
